import { Link } from "react-router-dom";
import { Clock, CheckCircle2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaSection } from "@/components/CtaSection";
import { FaqSection } from "@/components/FaqSection";
import { Testimonials } from "@/components/Testimonials";
import { SERVICES } from "@/data/services";
import { BAIRROS } from "@/data/bairros";
import { serviceUrl, bairroUrl, comboUrl } from "@/lib/routes";
import { serviceSchema, faqSchema, breadcrumbSchema, localBusinessSchema } from "@/lib/schema";

const comboFaq = (s, b) => [
  { q: `Quanto custa ${s.nome.toLowerCase()} ${b.prep} ${b.nome}?`, a: `O valor depende do tipo e gravidade da obstrução. Chame no WhatsApp (11) 97195-0197, descreva o problema e receba o orçamento fechado na hora, sem taxa de visita ${b.prep} ${b.nome}.` },
  { q: `Em quanto tempo vocês chegam ${b.prep} ${b.nome}?`, a: `Nosso tempo médio de chegada ${b.prep} ${b.nome} é de ${b.tempoChegada}, com equipes de plantão 24 horas posicionadas estrategicamente em Guarulhos.` },
  { q: `O serviço de ${s.nome.toLowerCase()} tem garantia?`, a: `Sim. Todo ${s.nome.toLowerCase()} realizado ${b.prep} ${b.nome} possui garantia formal registrada e nota fiscal. Se o problema retornar no prazo, voltamos sem custo.` },
  { q: `Vocês atendem ${b.prep} ${b.nome} de madrugada e nos feriados?`, a: `Sim, nosso plantão funciona 24 horas por dia, 7 dias por semana, inclusive feriados, em todos os endereços ${b.prep} ${b.nome} e região.` },
];

export default function ServicoBairroPage({ service, bairro }) {
  const url = comboUrl(service, bairro);
  const faq = comboFaq(service, bairro);
  const crumbs = [
    { name: "Início", url: "/" },
    { name: service.nome, url: serviceUrl(service) },
    { name: bairro.nome, url },
  ];
  const depoimentos = bairro.depoimentos.map((d) => ({ ...d, bairro: bairro.nome }));
  const outrosBairros = BAIRROS.filter((b) => b.slug !== bairro.slug).slice(0, 5);
  const outrosServicos = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 4);

  return (
    <main className="max-w-7xl mx-auto px-4 md:px-8" data-testid="combo-page">
      <Seo
        title={`${service.nome} ${bairro.prep} ${bairro.nome} 24h | (11) 97195-0197`}
        description={`${service.nome} ${bairro.prep} ${bairro.nome}, Guarulhos, com atendimento 24 horas e chegada em ${bairro.tempoChegada}. Garantia formal, nota fiscal e orçamento grátis pelo WhatsApp (11) 97195-0197.`}
        path={url}
        image={service.image}
        jsonLd={[serviceSchema(service.nome, `${service.nome} ${bairro.prep} ${bairro.nome}, Guarulhos, com atendimento 24 horas.`, url, bairro.nome), faqSchema(faq), breadcrumbSchema(crumbs), localBusinessSchema(bairro.nome)]}
      />
      <Breadcrumbs items={crumbs} />

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center py-6">
        <div>
          <span className="inline-flex items-center gap-2 bg-brand-yellow text-brand-dark text-xs font-heading font-black uppercase px-3 py-1.5 rounded-full mb-4">
            <Clock size={14} /> Chegada {bairro.prep} {bairro.nome} em {bairro.tempoChegada}
          </span>
          <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-dark uppercase tracking-tight leading-tight">
            {service.nome} {bairro.prep} <span className="text-brand-red">{bairro.nome}</span>
          </h1>
          <p className="mt-4 text-gray-600 text-sm md:text-base leading-relaxed">
            Precisa de <strong>{service.nome.toLowerCase()} {bairro.prep} {bairro.nome}</strong>? Nossa equipe de plantão atende seu endereço 24 horas por dia, com equipamentos profissionais, garantia formal e preço fechado no WhatsApp — sem taxa de visita e sem surpresa no final.
          </p>
        </div>
        <img src={service.image} alt={`${service.nome} ${bairro.prep} ${bairro.nome}, Guarulhos - técnico em atendimento`} width="1264" height="848" className="rounded-2xl shadow-lg w-full" />
      </section>

      <section className="py-6 text-gray-700 text-sm md:text-base leading-relaxed space-y-4 max-w-4xl" data-testid="combo-content">
        <h2 className="font-heading font-black text-2xl md:text-3xl text-brand-dark uppercase tracking-tight">Atendimento rápido {bairro.prep} {bairro.nome}, Guarulhos</h2>
        <p>{bairro.descricao}</p>
        <p>
          Atendemos toda a região, incluindo {bairro.ruas.slice(0, 3).join(", ")} e proximidades de {bairro.referencias.slice(0, 2).join(" e ")}. Entre os problemas mais comuns que resolvemos {bairro.prep} {bairro.nome} estão: {bairro.problemas.map((p) => p.toLowerCase()).join("; ")}.
        </p>
        <p>{service.descricao[1]}</p>
      </section>

      <section className="py-6" data-testid="combo-processo">
        <h2 className="font-heading font-black text-2xl md:text-3xl text-brand-dark uppercase tracking-tight mb-6">Como funciona o {service.nome.toLowerCase()} {bairro.prep} {bairro.nome}</h2>
        <ol className="space-y-4 max-w-4xl">
          {service.processo.map((p, i) => (
            <li key={i} className="flex items-start gap-4">
              <span className="w-9 h-9 rounded-full bg-brand-red text-white font-heading font-black flex items-center justify-center shrink-0">{i + 1}</span>
              <span className="text-sm md:text-base text-gray-700 pt-1.5">{p}</span>
            </li>
          ))}
        </ol>
        <div className="flex items-center gap-2 mt-6 text-sm md:text-base font-medium text-brand-dark">
          <CheckCircle2 size={20} className="text-brand-green" /> Garantia formal e nota fiscal em todos os atendimentos {bairro.prep} {bairro.nome}.
        </div>
      </section>

      <Testimonials depoimentos={depoimentos} titulo={`Clientes atendidos ${bairro.prep} ${bairro.nome}`} />

      <FaqSection faq={faq} titulo={`Dúvidas sobre ${service.nome.toLowerCase()} ${bairro.prep} ${bairro.nome}`} />

      <section className="py-6" data-testid="combo-links-internos">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h2 className="font-heading font-bold text-xl text-brand-dark uppercase tracking-tight mb-4">{service.nome} em outros bairros</h2>
            <div className="flex flex-wrap gap-2">
              {outrosBairros.map((b) => (
                <Link key={b.slug} to={comboUrl(service, b)} className="bg-brand-light hover:bg-brand-red hover:text-white text-brand-dark text-sm font-medium px-4 py-2 rounded-full transition-colors" data-testid={`combo-outro-bairro-${b.slug}`}>
                  {b.nome}
                </Link>
              ))}
              <Link to={serviceUrl(service)} className="bg-brand-red text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-brand-redDark transition-colors" data-testid="combo-link-servico-mae">
                {service.nome} em Guarulhos
              </Link>
            </div>
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl text-brand-dark uppercase tracking-tight mb-4">Outros serviços {bairro.prep} {bairro.nome}</h2>
            <div className="flex flex-wrap gap-2">
              {outrosServicos.map((s) => (
                <Link key={s.slug} to={comboUrl(s, bairro)} className="bg-brand-light hover:bg-brand-red hover:text-white text-brand-dark text-sm font-medium px-4 py-2 rounded-full transition-colors" data-testid={`combo-outro-servico-${s.slug}`}>
                  {s.nomeCurto}
                </Link>
              ))}
              <Link to={bairroUrl(bairro)} className="bg-brand-red text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-brand-redDark transition-colors" data-testid="combo-link-bairro-mae">
                Desentupidora {bairro.prep} {bairro.nome}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CtaSection
        titulo={`${service.nomeCurto} entupido ${bairro.prep} ${bairro.nome}?`}
        texto={`Equipe de plantão agora mesmo, com chegada em ${bairro.tempoChegada}. Orçamento gratuito e preço fechado pelo WhatsApp.`}
        waMsg={`Olá! Preciso de ${service.nome.toLowerCase()} ${bairro.prep} ${bairro.nome}, Guarulhos. Pode me passar um orçamento?`}
      />
    </main>
  );
}
